import { useRef, useState } from 'react';
import { PHONE_VOICEMAIL_ROUTE, Color, Size, firstLine, pageKindLineText, t, type ColumnDef } from '@bms2/shared';
import { Button } from '../../components/controls';
import { useNotice } from '../../components/controls/useNotice';
import { PageModule } from '../../shell/PageModule';
import { post } from '../records/api';
import { CHECKLIST_ITEMS_PATH } from '../todos/TodosPage';
import { CALL_COLUMNS, CallList, type CallListRow } from './CallList';
import { PlayLink } from './PlayLink';
import styles from './CallList.module.css';

export { PHONE_VOICEMAIL_ROUTE };

const column = (key: string): ColumnDef => CALL_COLUMNS.find((each) => each.key === key)!;

/** 🔒 Their number with Play and Done after it, the record, the transcript's first line, the time, the length and our number. */
export const VOICEMAIL_COLUMNS: readonly ColumnDef[] = [
  column('theirText'),
  column('recordName'),
  { key: 'transcriptLine', label: t('phonePages.col.transcript'), role: 'secondary', type: 'text', sortable: false },
  column('timeText'),
  column('lengthText'),
  column('ourText'),
];

/**
 * 🔒 Voicemail (decision 11860; the owner's P2a): the Voicemail rows of Recents, each with Play, the transcript's first
 * line and Done while the to-do it raised is open. Done completes that to-do through its own route and gate, and is
 * disabled while that request is in flight, so a double click posts it once (row T3726).
 */
export function VoicemailPage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const [noticeBanner, setNotice] = useNotice();
  /** The to-dos being completed now: the ref answers a second click at once, the state disables the button. */
  const flying = useRef(new Set<string>());
  const [completing, setCompleting] = useState<ReadonlySet<string>>(new Set());
  const done = async (row: CallListRow, reload: () => void) => {
    const todoId = row.todoId ?? '';
    if (flying.current.has(todoId)) return;
    flying.current.add(todoId);
    setCompleting(new Set(flying.current));
    const sent = await post(`${CHECKLIST_ITEMS_PATH}/${encodeURIComponent(todoId)}/complete`, {}, apiBaseUrl);
    flying.current.delete(todoId);
    setCompleting(new Set(flying.current));
    setNotice(sent.ok ? { tone: 'good', text: t('phonePages.voicemail.completed') } : { tone: 'error', text: sent.message });
    reload();
  };
  return (
    <PageModule title={t('phonePages.voicemail.title')} subtitle={pageKindLineText({ page: 'named', route: PHONE_VOICEMAIL_ROUTE })} icon="voicemail">
      {noticeBanner}
      <CallList
        list="voicemail"
        apiBaseUrl={apiBaseUrl}
        columns={VOICEMAIL_COLUMNS}
        recordType={t('phonePages.voicemail.title')}
        emptyAction={t('phonePages.voicemail.empty')}
        rowWords={(row) => ({ transcriptLine: firstLine(row.transcript ?? '') || t('phonePages.voicemail.noTranscript') })}
        cellMarker={(row, key, reload) =>
          key === 'theirText' ? (
            /* 🔨 The acts' clicks never reach the row, so Play and Done do not also open the call. */
            <span className={styles.acts} data-voicemail-acts={row.id} onClick={(event) => event.stopPropagation()}>
              {row.recordingUrl ? <PlayLink url={row.recordingUrl} /> : <span className={styles.quiet}>{t('phonePages.voicemail.noRecording')}</span>}
              {row.todoId && row.todoOpen ? <Button size={Size.Small} color={Color.Navy} text={[t('phonePages.voicemail.done')]} actions={[() => void done(row, reload)]} disabled={completing.has(row.todoId)} /> : null}
            </span>
          ) : null
        }
      />
    </PageModule>
  );
}
